import { z } from 'zod'
import { oc } from '@orpc/contract'

// Stedi provider record ids are UUIDs (the Providers API assigns them on create).
const STEDI_PROVIDER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NOT_A_STEDI_ID = 'Expected a Stedi provider id (a UUID)'

/** The wire value: a Stedi provider record id, or `null` to unlink. */
export const stediProviderIdSchema = z
  .string()
  .trim()
  .regex(STEDI_PROVIDER_ID, NOT_A_STEDI_ID)
  .nullable()

/**
 * The same rule for the Provider Operations form, where **blank stands for `null`** — one field
 * is both the link and the unlink, so it has to accept being emptied. Shared with the
 * wire schema so the rule and its message are authored once (`AGENTS.md`).
 */
export const stediProviderIdFieldSchema = z
  .string()
  .trim()
  .refine((value) => value === '' || STEDI_PROVIDER_ID.test(value), NOT_A_STEDI_ID)

/**
 * Links (or unlinks) a Practitioner to a Stedi provider record. The record is what gets
 * enrolled with payers (ERA routing by tax id + NPI), so this mapping is the prerequisite
 * for attributing Stedi's remittances and enrollment state to a member.
 *
 * `organizationId` is passed in rather than derived from the practitioner **so the picker
 * and the validator provably agree** — same reasoning as the Bridge mapping. Written by a
 * super admin or by a Care Coordinator of the member's Organization (the DoseSpot rule,
 * PD-1961); the NPI cross-check is what actually guards the mapping's correctness.
 *
 * **Test-mode caveat:** Stedi's enrollments product refuses `test_…` keys wholesale, so
 * with a test key the id cannot be checked against Stedi at all — it is format-checked and
 * stored, and `verified: false` says so. A live key gets the full Bridge treatment: the id
 * must exist in the organization's account and its NPI must match the member's.
 *
 * `null` unlinks: the identifier is removed. Stedi holds no mirror of the link (no
 * `externalId` field on its provider records), so there is nothing vendor-side to clear.
 */
export const setPractitionerStediProvider = oc
  .route({ method: 'POST', path: '/v2/practitioners/{practitionerId}/stedi-provider' })
  .input(
    z.object({
      practitionerId: z.string().min(1),
      organizationId: z.string().min(1),
      stediProviderId: stediProviderIdSchema,
    }),
  )
  .output(
    z.object({
      stediProviderId: z.string().nullable(),
      /**
       * Whether the id was confirmed against Stedi's account before saving. `false` only
       * happens under a test-mode key, where Stedi refuses to answer — the mapping is
       * saved as given and gets verified naturally once the organization goes live.
       */
      verified: z.boolean(),
    }),
  )
